(() => {
  const sortStorageKey = 'sokameja-sort:v1';

  const readStoredSort = () => {
    try {
      return window.sessionStorage.getItem(sortStorageKey) || '';
    } catch (_error) {
      return '';
    }
  };

  const storeSort = (value) => {
    if (!value) return;
    try {
      window.sessionStorage.setItem(sortStorageKey, value);
    } catch (_error) {
      // Sorting still works when browser storage is unavailable.
    }
  };

  const closeMenuItem = (item) => {
    const toggle = item.querySelector(':scope > .sokameja-menu__row [data-sokameja-menu-toggle]');
    const branch = item.querySelector(':scope > [data-sokameja-menu-branch]');
    if (!toggle || !branch) return;

    toggle.setAttribute('aria-expanded', 'false');
    branch.hidden = true;
    branch.querySelectorAll('[data-sokameja-menu-toggle]').forEach((childToggle) => {
      childToggle.setAttribute('aria-expanded', 'false');
    });
    branch.querySelectorAll('[data-sokameja-menu-branch]').forEach((childBranch) => {
      childBranch.hidden = true;
    });
  };

  const initializeCategoryTrees = (root = document) => {
    root.querySelectorAll('[data-sokameja-category-tree]').forEach((tree) => {
      if (tree.dataset.sokamejaReady === 'true') return;
      tree.dataset.sokamejaReady = 'true';

      const collectionMatch = window.location.pathname.match(/\/collections\/([^/]+)/);
      const activeHandle = collectionMatch ? decodeURIComponent(collectionMatch[1]) : '';
      const activeLink = Array.from(tree.querySelectorAll('[data-sokameja-category-link]')).find(
        (link) => link.dataset.categoryHandle === activeHandle
      );
      if (activeLink) {
        activeLink.setAttribute('aria-current', 'page');
        let item = activeLink.closest('[data-sokameja-menu-item]');
        while (item && tree.contains(item)) {
          item.classList.add('is-active-path');
          const toggle = item.querySelector(':scope > .sokameja-menu__row [data-sokameja-menu-toggle]');
          const branch = item.querySelector(':scope > [data-sokameja-menu-branch]');
          if (toggle && branch) {
            toggle.setAttribute('aria-expanded', 'true');
            branch.hidden = false;
          }
          const parentBranch = item.parentElement?.closest('[data-sokameja-menu-branch]');
          item = parentBranch?.closest('[data-sokameja-menu-item]') || null;
        }
      }

      tree.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-sokameja-menu-toggle]');
        if (!toggle || !tree.contains(toggle)) return;

        const item = toggle.closest('[data-sokameja-menu-item]');
        const branch = item?.querySelector(':scope > [data-sokameja-menu-branch]');
        if (!item || !branch) return;

        const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
        if (willOpen) {
          const siblingItems = Array.from(item.parentElement.children).filter((element) =>
            element.matches('[data-sokameja-menu-item]')
          );
          siblingItems.forEach((sibling) => {
            if (sibling !== item) closeMenuItem(sibling);
          });
        }

        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        branch.hidden = !willOpen;
      });
    });
  };

  const initializeSorting = (root = document) => {
    const main = document.getElementById('MainContent');
    const url = new URL(window.location.href);
    const urlSort = url.searchParams.get('sort_by');
    if (urlSort) storeSort(urlSort);
    else if (main?.dataset.template === 'index') storeSort('created-descending');

    const activeSort = urlSort || readStoredSort() || 'created-descending';
    root.querySelectorAll('[data-sokameja-category-link]').forEach((link) => {
      const linkUrl = new URL(link.href, window.location.origin);
      linkUrl.searchParams.set('sort_by', activeSort);
      const view = url.searchParams.get('view');
      if (view) linkUrl.searchParams.set('view', view);
      link.href = linkUrl.toString();
    });

    root.querySelectorAll('[data-sokameja-home-sort]').forEach((select) => {
      if (select.dataset.sokamejaSortReady === 'true') return;
      select.dataset.sokamejaSortReady = 'true';
      select.value = urlSort || 'created-descending';
      select.addEventListener('change', () => {
        storeSort(select.value);
        const destination = new URL('/', window.location.origin);
        ['preview_theme_id', '_fd', 'pb', 'view'].forEach((key) => {
          const value = url.searchParams.get(key);
          if (value) destination.searchParams.set(key, value);
        });
        destination.searchParams.set('sort_by', select.value);
        window.location.assign(destination.toString());
      });
    });
  };

  const rewriteHomePagination = (catalog, activeSort, explicitSort) => {
    const currentUrl = new URL(window.location.href);
    catalog.querySelectorAll('.pagination-wrapper a[href]').forEach((link) => {
      const source = new URL(link.href, window.location.origin);
      const destination = new URL('/', window.location.origin);
      const page = source.searchParams.get('page');
      if (page) destination.searchParams.set('page', page);
      ['preview_theme_id', '_fd', 'pb', 'view'].forEach((key) => {
        const value = currentUrl.searchParams.get(key);
        if (value) destination.searchParams.set(key, value);
      });
      if (explicitSort) destination.searchParams.set('sort_by', activeSort);
      link.href = destination.toString();
    });
  };

  const initializeHomeCatalog = async (root = document) => {
    const catalog = root.querySelector('[data-sokameja-home-catalog]');
    if (!catalog || catalog.dataset.sokamejaCatalogReady === 'true') return;
    catalog.dataset.sokamejaCatalogReady = 'true';

    const currentUrl = new URL(window.location.href);
    const explicitSort = currentUrl.searchParams.has('sort_by');
    const activeSort = currentUrl.searchParams.get('sort_by') || 'created-descending';
    const sourceUrl = new URL(catalog.dataset.sourceCollectionUrl || '/collections/all', window.location.origin);
    sourceUrl.searchParams.set('sort_by', activeSort);
    ['preview_theme_id', '_fd', 'pb', 'page', 'view'].forEach((key) => {
      const value = currentUrl.searchParams.get(key);
      if (value) sourceUrl.searchParams.set(key, value);
    });

    try {
      const response = await fetch(sourceUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Catalogue request failed: ${response.status}`);

      const sourceDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sourceGrid = sourceDocument.querySelector('#product-grid');
      const targetGrid = catalog.querySelector('#product-grid');
      if (!sourceGrid || !targetGrid) throw new Error('Catalogue grid was not returned');

      targetGrid.className = sourceGrid.className;
      targetGrid.innerHTML = sourceGrid.innerHTML;

      const sourcePagination = sourceDocument.querySelector('#ProductGridContainer .pagination-wrapper');
      const targetPagination = catalog.querySelector('.pagination-wrapper');
      if (sourcePagination && targetPagination) targetPagination.replaceWith(sourcePagination);
      else if (sourcePagination && !targetPagination) catalog.appendChild(sourcePagination);
      else if (!sourcePagination && targetPagination) targetPagination.remove();

      rewriteHomePagination(catalog, activeSort, explicitSort);
      document.dispatchEvent(new CustomEvent('sokameja:catalog-updated', { detail: { catalog } }));
    } catch (error) {
      console.error('Sokamėja homepage catalogue could not be refreshed.', error);
    } finally {
      catalog.classList.remove('is-loading');
      catalog.setAttribute('aria-busy', 'false');
    }
  };

  const initializePageSizeSelectors = (root = document) => {
    root.querySelectorAll('[data-sokameja-page-size]').forEach((select) => {
      if (select.dataset.sokamejaReady === 'true') return;
      select.dataset.sokamejaReady = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        if (select.value === '48') url.searchParams.delete('view');
        else url.searchParams.set('view', select.value);
        window.location.assign(url.toString());
      });
    });
  };

  const preserveViewParameter = (root = document) => {
    const view = new URL(window.location.href).searchParams.get('view');
    if (!view) return;

    root.querySelectorAll('facet-filters-form form').forEach((form) => {
      if (form.querySelector('input[name="view"]')) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'view';
      input.value = view;
      form.appendChild(input);
    });

    root.querySelectorAll('.facets-container a[href], .pagination a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('view', view);
      link.href = url.toString();
    });
  };

  const updateRailButtons = (rail) => {
    const viewport = rail.querySelector('[data-sokameja-rail-viewport]');
    const previous = rail.querySelector('[data-sokameja-rail-previous]');
    const next = rail.querySelector('[data-sokameja-rail-next]');
    if (!viewport || !previous || !next) return;

    const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    previous.disabled = viewport.scrollLeft <= 2;
    next.disabled = viewport.scrollLeft >= maximum - 2;
  };

  const initializeRails = (root = document) => {
    root.querySelectorAll('[data-sokameja-product-rail]').forEach((rail) => {
      if (rail.dataset.sokamejaReady === 'true') return;
      rail.dataset.sokamejaReady = 'true';

      const viewport = rail.querySelector('[data-sokameja-rail-viewport]');
      if (!viewport) return;
      rail.querySelector('[data-sokameja-rail-previous]')?.addEventListener('click', () => {
        viewport.scrollBy({ left: -viewport.clientWidth * 0.88, behavior: 'smooth' });
      });
      rail.querySelector('[data-sokameja-rail-next]')?.addEventListener('click', () => {
        viewport.scrollBy({ left: viewport.clientWidth * 0.88, behavior: 'smooth' });
      });
      viewport.addEventListener('scroll', () => updateRailButtons(rail), { passive: true });
      window.addEventListener('resize', () => updateRailButtons(rail));
      updateRailButtons(rail);
    });
  };

  const initialize = (root = document) => {
    initializeSorting(root);
    initializeHomeCatalog(root);
    initializeCategoryTrees(root);
    initializePageSizeSelectors(root);
    preserveViewParameter(root);
    initializeRails(root);
  };

  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest('select[name="sort_by"]');
      if (select) storeSort(select.value);
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('sokameja:rail-updated', (event) => updateRailButtons(event.detail.rail));

  const recommendationsObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) initialize();
    });
  });
  recommendationsObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
